import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import { ReactComponent as BankIcon } from '../../../assets/icons/svg/shared/ic-shared-bank.svg';
import Heading from '../../../generic-components/Heading';
import { amountContentMap, AmountLimitErrorMap, getAnalyticsFlowName, MoveMoneySteps, MoveMoneyTypes } from '../consts';
import { useStore } from '../../../store';
import { VIEWS_MAP } from '../../../router/constants';
import { LiliCurrencyInputExpandable } from '../../../DesignSystem/LiliCurrencyInputExpandable/LiliCurrencyInputExpandable';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { findLowestLimit } from '../utils';
import { NavigationHeader } from '../components/NavigationHeader';
import { trackMixPanel } from '../../../services/mixpanel.service';

export const MoveMoneyAmountStep = () => {
  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);
  const { balance } = useStore((state) => state.getPrimaryAccount());
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { moveType } = useParams();

  const [transferAmount, setTransferAmount] = useState<number | null>(moveMoneyData.amount || null);
  const [errMsg, setErrMsg] = useState<string>('');
  const [amountLimit] = useState<{ key: string; value: number }>(() => {
    const limit = findLowestLimit(moveMoneyData?.limitsData);
    return { key: limit.key.toString(), value: limit.value };
  });

  const handleBackButton = () => {
    trackMixPanel('Act on Move Money Amount Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Back',
    });
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.VENDOR_PICKER });
  };

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleContinue = () => {
    if (!transferAmount) return;
    trackMixPanel('Act on Move Money Amount Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Continue',
      Amount: transferAmount,
    });
    if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT && !moveMoneyData.isEditMode) {
      setMoveMoneyData({ ...moveMoneyData, amount: transferAmount, activeStepId: MoveMoneySteps.PURPOSE_OF_TRANSFER });
    } else {
      setMoveMoneyData({ ...moveMoneyData, amount: transferAmount, activeStepId: MoveMoneySteps.REVIEW });
    }
  };

  const getErrorId = () => {
    if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) return 'achOutAmountPageInputError';
    if (moveType === MoveMoneyTypes.WIRE_OUT) return 'wireOutAmountPageInputError';
    if (moveType === MoveMoneyTypes.MAIL_CHECK) return 'mailCheckAmountPageInputError';
    return 'amountPageInputError';
  };

  useEffect(() => {
    if (!transferAmount) {
      setErrMsg('');
      return;
    }
    if (amountLimit.value < transferAmount) {
      setErrMsg(t(AmountLimitErrorMap[amountLimit.key as keyof typeof AmountLimitErrorMap], { amount: convertValueToUSD(amountLimit.value, 2) }));
    } else {
      setErrMsg('');
    }
  }, [transferAmount]);

  useEffect(() => {
    trackMixPanel('View Move Money Amount Screen', { Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes) });
  }, []);

  return (
    <div className='absolute left-0 top-0 w-full h-full bg-bw00 z-50 flex flex-col'>
      <NavigationHeader
        shouldDirectClose
        hideBackButton={moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT ? false : moveMoneyData.isEditMode}
        onClose={handleCloseFlow}
        onBack={handleBackButton}
      />
      <Heading
        type='HTML'
        title={t(amountContentMap[moveType as keyof typeof amountContentMap].content)}
        className='mx-auto text-center mb-[8px] font-metricMedium text-bw08 text-[24px]'
      />
      <Heading type='HTML' title={`To ${moveMoneyData.vendorData?.name}`} className='mx-auto text-center mb-[68px] font-metricR text-bw08 text-[16px]' />
      <div className='mx-auto flex flex-col gap-[10px] mb-[100px] relative'>
        <LiliCurrencyInputExpandable id='inputAmountToTransfer' value={transferAmount} setValue={(value) => setTransferAmount(value)} />
        <p id={getErrorId()} className='text-red02 font-metricR text-[14px] text-center w-full absolute left-[50%] bottom-0 translate-y-[110%] translate-x-[-50%] fadeIn'>{errMsg}</p>
      </div>
      <div className='w-[400px] mx-auto flex items-center justify-between'>
        <div className='flex items-center gap-[6px]'>
          <BankIcon className='text-bw07' />
          <Heading title={t('MOVE_MONEY.STEPS.AMOUNT.BALANCE_TYPE')} className='font-metricR text-[14px] text-bw07' />
        </div>
        <div className='flex flex-col items-end gap-[4px]'>
          <Heading title={t('MOVE_MONEY.STEPS.AMOUNT.AVAILABLE_BALANCE')} className='font-metricR text-[12px] text-bw07' />
          <Heading id='achOutAmountPageAvailableBalance' title={convertValueToUSD(balance, 2)} className='font-metricRMedium text-[16px] text-action03' />
        </div>
      </div>
      <div className='absolute left-0 bottom-0 w-full border-t border-bw03 flex items-center justify-center py-[24px]'>
        <LiliButton
          onClick={handleContinue}
          disabled={!!errMsg || !transferAmount}
          id='continue'
          className='w-[300px]'
          title={moveMoneyData.isEditMode ? t('MOVE_MONEY.STEPS.AMOUNT.CTA_EDIT') : t('MOVE_MONEY.STEPS.AMOUNT.CTA')}
        />
      </div>
    </div>
  );
};
